// Live API service on Railway (project: thriving-delight, service: Kodtutor, root /api).
const DEFAULT_API_URL = 'https://kodtutor-production.up.railway.app';

function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  if (configured?.trim()) {
    return configured.replace(/\/$/, '');
  }
  // In Vite dev, use the /api proxy (see vite.config.ts → API_PROXY_TARGET).
  if (import.meta.env.DEV) {
    return '/api';
  }
  return DEFAULT_API_URL;
}

export const API_BASE_URL = resolveApiBaseUrl();

const SESSION_TOKEN_KEY = 'sessionToken';

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function setSessionToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearSessionToken(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  data?: Record<string, unknown>;

  constructor(message: string, status: number, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

type ApiRequestOptions = RequestInit & {
  auth?: boolean;
};

export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { auth = false, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);

  if (fetchOptions.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (auth) {
    const token = getSessionToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  let data: Record<string, unknown> = {};
  const contentType = response.headers.get('content-type');
  const rawText = await response.text();

  if (contentType?.includes('application/json') && rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      throw new ApiError('Invalid JSON response from API', response.status);
    }
  } else if (!response.ok && rawText) {
    throw new ApiError(
      rawText.startsWith('<!DOCTYPE')
        ? `API endpoint not found (${response.status}). Deploy the latest API or run it locally with API_PROXY_TARGET=http://127.0.0.1:8080.`
        : rawText.slice(0, 200),
      response.status,
    );
  }

  if (!response.ok) {
    throw new ApiError(
      (data.error as string) || `Request failed (${response.status})`,
      response.status,
      Object.keys(data).length > 0 ? data : undefined,
    );
  }

  return data as T;
}
